/**
 * MMOAsteroid - MMO mode asteroid entity
 * Displays asteroids using sprite images with glow/sparkle effects
 * scaled by mineral richness and rarity.
 */

import Phaser from 'phaser';
import { axialToPixel, type HexCoord } from '../../shared/HexUtils';
import type { MMOAsteroid as MMOAsteroidData } from '../../managers/MMOStateManager';

const PLAYER_COLORS = [
  0x3388ee, 0xee4444, 0x44cc55, 0xeeee44,
  0xf08822, 0xbb55ee, 0x00ffff, 0xff00ff,
];

// Mineral type -> sprite key mapping
const MINERAL_SPRITE_MAP: Record<string, string> = {
  min_iron: 'asteroid-iron',
  min_copper: 'asteroid-copper',
  min_titanium: 'asteroid-ice',
  min_platinum: 'asteroid-platinum',
  min_rhodium: 'asteroid-gold',
  min_palladium: 'asteroid-rare',
  min_iridium: 'asteroid-rare-crystal',
  min_darkmat: 'asteroid-dark-matter',
};

// Richness-tier fallback sprite pools (when no dominant mineral)
const RICHNESS_SPRITE_POOL: [number, string[]][] = [
  [80, ['asteroid-exotic', 'asteroid-dark-matter', 'asteroid-rare-crystal']],
  [60, ['asteroid-rare-crystal', 'asteroid-platinum', 'asteroid-rare']],
  [40, ['asteroid-rare', 'asteroid-volatile', 'asteroid-copper']],
  [20, ['asteroid-iron', 'asteroid-copper', 'asteroid-ice']],
  [0, ['asteroid-common', 'asteroid-iron']],
];

// Golden variant -> sprite key
const VARIANT_SPRITE_MAP: Record<string, string> = {
  common_gold: 'asteroid-gold',
  rare_platinum: 'asteroid-platinum',
  legendary_dark_matter: 'asteroid-dark-matter',
  welcome_gift: 'asteroid-exotic',
};

// Glow color per richness tier
const RICHNESS_GLOW: [number, { color: number; alpha: number; radius: number; pulseSpeed: number }][] = [
  [80, { color: 0xcc66ff, alpha: 0.35, radius: 38, pulseSpeed: 1200 }],
  [60, { color: 0xffcc33, alpha: 0.25, radius: 34, pulseSpeed: 1600 }],
  [40, { color: 0xddaa44, alpha: 0.15, radius: 30, pulseSpeed: 2000 }],
  [20, { color: 0x88aacc, alpha: 0.08, radius: 28, pulseSpeed: 2400 }],
];

// Variant glow config
const VARIANT_GLOW: Record<string, { color: number; alpha: number; radius: number; pulseSpeed: number }> = {
  common_gold: { color: 0xffd700, alpha: 0.30, radius: 36, pulseSpeed: 1000 },
  rare_platinum: { color: 0x88aadd, alpha: 0.35, radius: 38, pulseSpeed: 800 },
  legendary_dark_matter: { color: 0xaa44ff, alpha: 0.45, radius: 42, pulseSpeed: 600 },
  welcome_gift: { color: 0x00ff88, alpha: 0.30, radius: 36, pulseSpeed: 700 },
};

export class MMOAsteroid extends Phaser.GameObjects.Container {
  public id: string;
  public ownerId: string | null;
  public hexCoord: HexCoord;

  public resourceRichness: number;
  public miningLevel: number;
  public storedResources: number;
  public maxStorage: number;

  public asteroidType: 'regular' | 'golden' | 'welcome' | 'special' | 'crypto';
  public variant: 'common_gold' | 'rare_platinum' | 'legendary_dark_matter' | 'welcome_gift';
  public rewardValue: number | null;
  public cryptoToken: string | null;
  public cryptoChain: string | null;
  public mineralComposition: Record<string, number> | null;

  // Visual elements
  private asteroidSprite: Phaser.GameObjects.Image | null = null;
  private glowGraphics: Phaser.GameObjects.Graphics;
  private ownerRing: Phaser.GameObjects.Graphics;
  private selectionIndicator: Phaser.GameObjects.Graphics;
  private resourceLabel: Phaser.GameObjects.Text;
  private glowTween?: Phaser.Tweens.Tween;

  // Movement smoothing
  private moveTween: Phaser.Tweens.Tween | null = null;
  private isFirstPosition: boolean = true;

  // Orbiting sparkles for rich/golden asteroids
  private sparkleGraphics: Phaser.GameObjects.Graphics | null = null;
  private sparkleAngle: number = 0;
  private sparkleConfig: { count: number; color: number; radius: number; speed: number } | null = null;
  private sparkleTimer: Phaser.Time.TimerEvent | null = null;

  // Active mining indicator
  private miningIndicator: Phaser.GameObjects.Graphics | null = null;
  private miningIndicatorTween: Phaser.Tweens.Tween | null = null;
  private miningFadeTimer: Phaser.Time.TimerEvent | null = null;
  private miningProgressArc: Phaser.GameObjects.Graphics | null = null;
  private lastMiningProgress: number = 0;

  // Overcrowding warning visuals
  private warningShakeTween: Phaser.Tweens.Tween | null = null;
  private warningFlash: Phaser.GameObjects.Arc | null = null;
  private warningFlashTween: Phaser.Tweens.Tween | null = null;

  // Ambient rotation
  private rotationTween: Phaser.Tweens.Tween | null = null;

  // Resolved sprite key (stable per asteroid for consistent appearance)
  private spriteKey: string = 'asteroid-common';

  constructor(scene: Phaser.Scene, data: MMOAsteroidData) {
    const pixel = axialToPixel(data.position.q, data.position.r);
    super(scene, pixel.x, pixel.y);

    this.id = data.id;
    this.ownerId = data.ownerId;
    this.hexCoord = data.position;
    this.resourceRichness = data.resourceRichness;
    this.miningLevel = data.miningLevel;
    this.storedResources = data.storedResources;
    this.maxStorage = data.maxStorage;
    this.asteroidType = data.asteroidType || 'regular';
    this.variant = data.variant || 'common_gold';
    this.rewardValue = data.rewardValue ?? null;
    this.cryptoToken = data.cryptoToken ?? null;
    this.cryptoChain = data.cryptoChain ?? null;
    this.mineralComposition = data.mineralComposition || null;

    // Resolve sprite key once (stable — seeded by asteroid id)
    this.spriteKey = this.resolveSpriteKey();

    // Glow layer (behind sprite)
    this.glowGraphics = this.scene.add.graphics();
    this.add(this.glowGraphics);

    // Owner ring
    this.ownerRing = this.scene.add.graphics();
    this.add(this.ownerRing);

    // Selection indicator
    this.selectionIndicator = this.scene.add.graphics();
    this.selectionIndicator.setVisible(false);
    this.add(this.selectionIndicator);

    // Resource label
    this.resourceLabel = this.scene.add.text(0, 25, '', {
      fontSize: '10px',
      color: '#ffffff',
    });
    this.resourceLabel.setOrigin(0.5);
    this.resourceLabel.setVisible(false);
    this.add(this.resourceLabel);

    // Draw everything
    this.drawVisuals();

    // Sparkles for golden/welcome and rich regular asteroids
    if (this.asteroidType === 'golden' || this.asteroidType === 'crypto' || this.asteroidType === 'welcome' || this.asteroidType === 'special') {
      this.setupOrbitingSparkles();
    } else if (this.resourceRichness >= 60) {
      this.setupRichnessSparkles();
    }

    // Ambient slow rotation on the sprite
    if (this.asteroidSprite) {
      this.rotationTween = this.scene.tweens.add({
        targets: this.asteroidSprite,
        angle: 360,
        duration: 25000 + this.seededRandom() * 10000,
        repeat: -1,
      });
    }

    this.setDepth(5);
    this.setSize(50, 50);
    this.setInteractive(new Phaser.Geom.Rectangle(-25, -25, 50, 50), Phaser.Geom.Rectangle.Contains);

    // NOTE: No sprite-level 'pointerdown' handler — see MMOUnit.ts for rationale.
    // Selection is handled in MMOGameScene.onPointerUp via getEntityAtWorldPoint().
  }

  public updateFromServer(data: MMOAsteroidData): void {
    if (!this.scene || !this.scene.sys) return;

    this.ownerId = data.ownerId;
    this.resourceRichness = data.resourceRichness;
    this.miningLevel = data.miningLevel;
    this.storedResources = data.storedResources;
    if (data.maxStorage > this.maxStorage) {
      this.maxStorage = data.maxStorage;
    }
    this.asteroidType = data.asteroidType || 'regular';
    this.variant = data.variant || 'common_gold';
    this.rewardValue = data.rewardValue ?? this.rewardValue;
    if (data.cryptoToken) this.cryptoToken = data.cryptoToken;
    if (data.cryptoChain) this.cryptoChain = data.cryptoChain;
    if (data.mineralComposition !== undefined) {
      this.mineralComposition = data.mineralComposition || null;
    }

    if (data.position.q !== this.hexCoord.q || data.position.r !== this.hexCoord.r) {
      this.hexCoord = data.position;
      this.updateVisualPosition();
    }

    this.drawVisuals();
  }

  public setVisibility(fullyVisible: boolean, explored: boolean): void {
    if (!explored) {
      this.setAlpha(0);
    } else if (!fullyVisible) {
      this.setAlpha(0.4);
    } else {
      this.setAlpha(1);
    }
  }

  public setTweenActive(active: boolean): void {
    if (active) {
      if (this.rotationTween?.isPaused()) this.rotationTween.resume();
      if (this.glowTween?.isPaused()) this.glowTween.resume();
    } else {
      if (this.rotationTween?.isPlaying()) this.rotationTween.pause();
      if (this.glowTween?.isPlaying()) this.glowTween.pause();
    }
  }

  public drawVisuals(): void {
    this.drawSprite();
    this.drawGlow();
    this.applyDepletionEffect();
    this.drawOwnerRing();
    this.drawSelectionIndicator();
    this.updateResourceLabel();
  }

  // ── Sprite rendering ───────────────────────────────────────

  private drawSprite(): void {
    const key = this.resolveSpriteKey();

    if (this.asteroidSprite) {
      if (this.asteroidSprite.texture.key === key) return;
      this.asteroidSprite.destroy();
      this.asteroidSprite = null;
    }

    if (!this.scene.textures.exists(key)) {
      this.spriteKey = 'asteroid-common';
    } else {
      this.spriteKey = key;
    }

    const spriteSize = this.getSpriteSize();
    this.asteroidSprite = this.scene.add.image(0, -4, this.spriteKey);
    this.asteroidSprite.setDisplaySize(spriteSize, spriteSize);
    this.add(this.asteroidSprite);

    // Move sprite behind glow layer but in front of owner ring
    this.moveTo(this.asteroidSprite, 2);
  }

  private getSpriteSize(): number {
    if (this.asteroidType === 'golden' || this.asteroidType === 'crypto' || this.asteroidType === 'welcome' || this.asteroidType === 'special') {
      switch (this.variant) {
        case 'legendary_dark_matter': return 52;
        case 'rare_platinum': return 46;
        case 'welcome_gift': return 48;
        default: return 44;
      }
    }
    if (this.resourceRichness >= 80) return 48;
    if (this.resourceRichness >= 60) return 44;
    if (this.resourceRichness >= 40) return 40;
    if (this.resourceRichness >= 20) return 36;
    return 32;
  }

  // ── Glow effect ────────────────────────────────────────────

  private drawGlow(): void {
    this.glowGraphics.clear();

    let glowCfg: { color: number; alpha: number; radius: number; pulseSpeed: number } | null = null;

    if (this.asteroidType === 'golden' || this.asteroidType === 'crypto' || this.asteroidType === 'welcome' || this.asteroidType === 'special') {
      glowCfg = VARIANT_GLOW[this.variant] || VARIANT_GLOW.common_gold;
    } else {
      for (const [threshold, cfg] of RICHNESS_GLOW) {
        if (this.resourceRichness >= threshold) {
          glowCfg = cfg;
          break;
        }
      }
    }

    if (!glowCfg) return;

    // Soft radial glow — two concentric circles with fading alpha
    this.glowGraphics.fillStyle(glowCfg.color, glowCfg.alpha * 0.4);
    this.glowGraphics.fillCircle(0, -4, glowCfg.radius);
    this.glowGraphics.fillStyle(glowCfg.color, glowCfg.alpha);
    this.glowGraphics.fillCircle(0, -4, glowCfg.radius * 0.6);

    // Pulsing glow animation
    if (!this.glowTween) {
      this.glowTween = this.scene.tweens.add({
        targets: this.glowGraphics,
        alpha: { from: 0.6, to: 1.0 },
        duration: glowCfg.pulseSpeed,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  // ── Depletion dimming ──────────────────────────────────────

  private applyDepletionEffect(): void {
    if (this.asteroidType !== 'regular' || this.maxStorage <= 0) return;

    const remaining = this.mineralComposition
      ? Object.values(this.mineralComposition).reduce((s, v) => s + (Number(v) || 0), 0)
      : this.storedResources;
    const ratio = remaining / this.maxStorage;

    if (ratio < 0.2 && this.asteroidSprite) {
      const alpha = 0.4 + (ratio / 0.2) * 0.6;
      this.asteroidSprite.setAlpha(alpha);
    } else if (this.asteroidSprite) {
      this.asteroidSprite.setAlpha(1);
    }
  }

  // ── Sprite key resolution ──────────────────────────────────

  private resolveSpriteKey(): string {
    if (this.asteroidType === 'golden' || this.asteroidType === 'crypto' || this.asteroidType === 'welcome' || this.asteroidType === 'special') {
      return VARIANT_SPRITE_MAP[this.variant] || 'asteroid-gold';
    }

    // Pick by dominant mineral
    if (this.mineralComposition) {
      let maxMineral = '';
      let maxAmount = 0;
      for (const [mineral, amount] of Object.entries(this.mineralComposition)) {
        const num = Number(amount) || 0;
        if (num > maxAmount) {
          maxAmount = num;
          maxMineral = mineral;
        }
      }
      if (maxMineral && MINERAL_SPRITE_MAP[maxMineral]) {
        return MINERAL_SPRITE_MAP[maxMineral];
      }
    }

    // Fallback by richness tier — deterministic pick seeded by asteroid id
    for (const [threshold, pool] of RICHNESS_SPRITE_POOL) {
      if (this.resourceRichness >= threshold) {
        const idx = this.seededIndex(pool.length);
        return pool[idx];
      }
    }

    return 'asteroid-common';
  }

  /** Deterministic 0..1 float seeded from asteroid id */
  private seededRandom(): number {
    let hash = 0;
    for (let i = 0; i < this.id.length; i++) {
      hash = (hash << 5) - hash + this.id.charCodeAt(i);
      hash = hash & hash;
    }
    return (Math.abs(hash) % 10000) / 10000;
  }

  /** Deterministic index into an array, seeded from asteroid id */
  private seededIndex(length: number): number {
    return Math.floor(this.seededRandom() * length) % length;
  }

  // ── Owner ring ─────────────────────────────────────────────

  private drawOwnerRing(): void {
    this.ownerRing.clear();

    if (this.ownerId) {
      const color = this.getOwnerColor();
      let alpha = 0.4;
      if (this.miningLevel > 0) {
        alpha = 0.5 + this.miningLevel * 0.15;
      }
      this.ownerRing.lineStyle(3, color, alpha);
      this.ownerRing.strokeCircle(0, 0, 18);
    }
  }

  private drawSelectionIndicator(): void {
    this.selectionIndicator.clear();
    this.selectionIndicator.lineStyle(3, 0x44ff44, 1);
    this.selectionIndicator.strokeCircle(0, 0, 22);
  }

  private updateResourceLabel(): void {
    if (this.asteroidType === 'golden' || this.asteroidType === 'crypto' || this.asteroidType === 'welcome' || this.asteroidType === 'special') {
      const label = this.getVariantLabel();
      this.resourceLabel.setText(label.text);
      this.resourceLabel.setColor(label.color);
    } else {
      this.resourceLabel.setText('');
    }
  }

  private getVariantLabel(): { text: string; color: string } {
    if (this.asteroidType === 'special') return { text: 'Crypto', color: '#00ddff' };
    switch (this.variant) {
      case 'legendary_dark_matter': return { text: 'Dark Matter', color: '#cc66ff' };
      case 'rare_platinum': return { text: 'Platinum', color: '#88aadd' };
      case 'welcome_gift': return { text: 'Welcome Gift', color: '#00ff88' };
      default: return { text: 'Gold', color: '#ffd700' };
    }
  }

  // ── Sparkles ───────────────────────────────────────────────

  private setupOrbitingSparkles(): void {
    const sparkleCount = this.variant === 'legendary_dark_matter' ? 6 :
                         this.variant === 'rare_platinum' ? 4 : 3;
    const sparkleColor = VARIANT_GLOW[this.variant]?.color || 0xffffaa;
    const orbitRadius = this.getSpriteSize() / 2 + 8;
    const orbitSpeed = this.variant === 'legendary_dark_matter' ? 2000 : 3000;

    this.sparkleGraphics = this.scene.add.graphics();
    this.add(this.sparkleGraphics);

    this.sparkleConfig = { count: sparkleCount, color: sparkleColor, radius: orbitRadius, speed: orbitSpeed };
    this.sparkleAngle = 0;
    this.redrawSparkles();

    this.sparkleTimer = this.scene.time.addEvent({
      delay: 100,
      loop: true,
      callback: () => {
        if (!this.sparkleGraphics || !this.sparkleConfig || !this.scene) return;
        this.sparkleAngle += (360 / (this.sparkleConfig.speed / 100)) * (Math.PI / 180);
        if (this.sparkleAngle > Math.PI * 2) this.sparkleAngle -= Math.PI * 2;
        this.redrawSparkles();
      },
    });
  }

  private setupRichnessSparkles(): void {
    const sparkleCount = this.resourceRichness >= 80 ? 4 : 2;
    const sparkleColor = this.resourceRichness >= 80 ? 0xcc66ff : 0xffdd66;
    const orbitRadius = this.getSpriteSize() / 2 + 6;
    const orbitSpeed = this.resourceRichness >= 80 ? 2500 : 4000;

    this.sparkleGraphics = this.scene.add.graphics();
    this.add(this.sparkleGraphics);

    this.sparkleConfig = { count: sparkleCount, color: sparkleColor, radius: orbitRadius, speed: orbitSpeed };
    this.sparkleAngle = 0;
    this.redrawSparkles();

    this.sparkleTimer = this.scene.time.addEvent({
      delay: 100,
      loop: true,
      callback: () => {
        if (!this.sparkleGraphics || !this.sparkleConfig || !this.scene) return;
        this.sparkleAngle += (360 / (this.sparkleConfig.speed / 100)) * (Math.PI / 180);
        if (this.sparkleAngle > Math.PI * 2) this.sparkleAngle -= Math.PI * 2;
        this.redrawSparkles();
      },
    });
  }

  private redrawSparkles(): void {
    if (!this.sparkleGraphics || !this.sparkleConfig) return;
    this.sparkleGraphics.clear();
    const { count, color, radius } = this.sparkleConfig;
    for (let i = 0; i < count; i++) {
      const angle = this.sparkleAngle + (Math.PI * 2 * i) / count;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius - 4;
      this.sparkleGraphics.fillStyle(color, 0.85);
      this.sparkleGraphics.fillCircle(x, y, 1.8);
      // Tiny trailing dot
      const trailAngle = angle - 0.3;
      this.sparkleGraphics.fillStyle(color, 0.35);
      this.sparkleGraphics.fillCircle(Math.cos(trailAngle) * radius, Math.sin(trailAngle) * radius - 4, 1);
    }
  }

  // ── Position & selection ───────────────────────────────────

  public updateVisualPosition(): void {
    const pixel = axialToPixel(this.hexCoord.q, this.hexCoord.r);

    if (this.isFirstPosition) {
      this.isFirstPosition = false;
      this.setPosition(pixel.x, pixel.y);
      return;
    }

    if (this.moveTween) {
      this.moveTween.destroy();
      this.moveTween = null;
    }

    this.moveTween = this.scene.tweens.add({
      targets: this,
      x: pixel.x,
      y: pixel.y,
      duration: 500,
      ease: 'Power2',
    });
  }

  public setSelected(selected: boolean): void {
    this.selectionIndicator.setVisible(selected);
  }

  // ── Mining effects ─────────────────────────────────────────

  public playMineEffect(): void {
    if (!this.scene) return;

    const burstColor = (this.asteroidType === 'golden' || this.asteroidType === 'crypto' || this.asteroidType === 'special') ? 0xFFD700 : this.asteroidType === 'welcome' ? 0x00ff88 : 0x88ccff;
    const particleCount = 8;
    const burstRadius = 30;

    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount + Math.random() * 0.4;
      const dist = burstRadius * (0.6 + Math.random() * 0.4);
      const g = this.scene.add.graphics();
      g.fillStyle(burstColor, 0.9);
      g.fillCircle(0, 0, 2 + Math.random() * 2);
      this.add(g);

      this.scene.tweens.add({
        targets: g,
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist,
        alpha: 0,
        duration: 400 + Math.random() * 200,
        ease: 'Quad.easeOut',
        onComplete: () => g.destroy(),
      });
    }

    this.scene.tweens.add({
      targets: this,
      scaleX: 1.15,
      scaleY: 1.15,
      duration: 100,
      yoyo: true,
      ease: 'Quad.easeOut',
    });

    this.showMiningActive();
  }

  public showMiningIndicator(): void { this.showMiningActive(); }
  public hideMiningIndicator(): void { this.hideMiningActive(); }

  public updateMiningProgress(progress: number): void {
    if (!this.scene) return;
    const clamped = Math.max(0, Math.min(1, progress));

    if (clamped <= 0) {
      if (this.miningProgressArc) {
        this.miningProgressArc.clear();
        this.miningProgressArc.setVisible(false);
      }
      this.lastMiningProgress = 0;
      return;
    }

    if (!this.miningProgressArc) {
      this.miningProgressArc = this.scene.add.graphics();
      this.add(this.miningProgressArc);
    }

    const ringColor = (this.asteroidType === 'golden' || this.asteroidType === 'crypto' || this.asteroidType === 'special') ? 0xFFD700 : this.asteroidType === 'welcome' ? 0x00ff88 : 0x00ccff;
    const radius = 30;
    const startAngle = -Math.PI / 2;
    const endAngle = startAngle + (Math.PI * 2 * clamped);

    this.miningProgressArc.clear();
    this.miningProgressArc.setVisible(true);

    this.miningProgressArc.lineStyle(3, ringColor, 0.15);
    this.miningProgressArc.beginPath();
    this.miningProgressArc.arc(0, 0, radius, 0, Math.PI * 2, false);
    this.miningProgressArc.strokePath();

    this.miningProgressArc.lineStyle(3, ringColor, 0.9);
    this.miningProgressArc.beginPath();
    this.miningProgressArc.arc(0, 0, radius, startAngle, endAngle, false);
    this.miningProgressArc.strokePath();

    if (clamped < this.lastMiningProgress && this.lastMiningProgress > 0.8) {
      this.miningProgressArc.lineStyle(4, 0xffffff, 1);
      this.miningProgressArc.beginPath();
      this.miningProgressArc.arc(0, 0, radius, 0, Math.PI * 2, false);
      this.miningProgressArc.strokePath();
      this.scene.tweens.add({
        targets: this.miningProgressArc,
        alpha: { from: 1, to: 0.7 },
        duration: 200,
        yoyo: true,
        ease: 'Quad.easeOut',
      });
    }

    this.lastMiningProgress = clamped;
  }

  private showMiningActive(): void {
    if (!this.scene) return;

    const ringColor = (this.asteroidType === 'golden' || this.asteroidType === 'crypto' || this.asteroidType === 'special') ? 0xFFD700 : this.asteroidType === 'welcome' ? 0x00ff88 : 0x00ccff;

    if (!this.miningIndicator) {
      this.miningIndicator = this.scene.add.graphics();
      this.add(this.miningIndicator);
    }

    this.miningIndicator.clear();
    this.miningIndicator.lineStyle(2, ringColor, 0.8);
    this.miningIndicator.strokeCircle(0, 0, 28);
    this.miningIndicator.lineStyle(1, ringColor, 0.4);
    this.miningIndicator.strokeCircle(0, 0, 34);
    this.miningIndicator.setAlpha(1);
    this.miningIndicator.setVisible(true);

    if (!this.miningIndicatorTween || !this.miningIndicatorTween.isPlaying()) {
      this.miningIndicatorTween?.destroy();
      this.miningIndicatorTween = this.scene.tweens.add({
        targets: this.miningIndicator,
        alpha: { from: 1, to: 0.3 },
        duration: 600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    if (this.miningFadeTimer) {
      this.miningFadeTimer.destroy();
    }
    this.miningFadeTimer = this.scene.time.delayedCall(3000, () => {
      this.hideMiningActive();
    });
  }

  private hideMiningActive(): void {
    if (this.miningIndicatorTween) {
      this.miningIndicatorTween.destroy();
      this.miningIndicatorTween = null;
    }
    if (this.miningIndicator) {
      this.scene?.tweens.add({
        targets: this.miningIndicator,
        alpha: 0,
        duration: 500,
        onComplete: () => {
          this.miningIndicator?.setVisible(false);
        },
      });
    }
  }

  // ── Overcrowding warning ───────────────────────────────────

  public setOvercrowdingWarning(active: boolean, _ticksRemaining?: number): void {
    if (!this.scene) return;

    if (active) {
      if (!this.warningShakeTween && this.asteroidSprite) {
        this.warningShakeTween = this.scene.tweens.add({
          targets: this.asteroidSprite,
          x: { from: -3, to: 3 },
          duration: 100,
          yoyo: true,
          repeat: -1,
        });
      }
      if (!this.warningFlash) {
        this.warningFlash = this.scene.add.circle(0, 0, 20, 0xff4444, 0.3);
        this.add(this.warningFlash);
        this.warningFlashTween = this.scene.tweens.add({
          targets: this.warningFlash,
          alpha: { from: 0.1, to: 0.4 },
          duration: 300,
          yoyo: true,
          repeat: -1,
        });
      }
    } else {
      if (this.warningShakeTween) {
        this.warningShakeTween.destroy();
        this.warningShakeTween = null;
        if (this.asteroidSprite) this.asteroidSprite.setX(0);
      }
      if (this.warningFlashTween) {
        this.warningFlashTween.destroy();
        this.warningFlashTween = null;
      }
      if (this.warningFlash) {
        this.warningFlash.destroy();
        this.warningFlash = null;
      }
    }
  }

  // ── Helpers ────────────────────────────────────────────────

  private getOwnerColor(): number {
    if (!this.ownerId) return 0x888888;
    let hash = 0;
    for (let i = 0; i < this.ownerId.length; i++) {
      hash = (hash << 5) - hash + this.ownerId.charCodeAt(i);
      hash = hash & hash;
    }
    return PLAYER_COLORS[Math.abs(hash) % PLAYER_COLORS.length];
  }

  /** Expose the resolved sprite key for UI (CommandDock) */
  public getSpriteKey(): string {
    return this.spriteKey;
  }

  // ── Cleanup ────────────────────────────────────────────────

  public destroy(fromScene?: boolean): void {
    if (this.moveTween) this.moveTween.destroy();
    if (this.sparkleTimer) this.sparkleTimer.destroy();
    if (this.sparkleGraphics) {
      this.sparkleGraphics.destroy();
      this.sparkleGraphics = null;
    }
    this.sparkleConfig = null;
    if (this.rotationTween) this.rotationTween.destroy();
    if (this.glowTween) this.glowTween.destroy();
    if (this.miningIndicatorTween) this.miningIndicatorTween.destroy();
    if (this.miningIndicator) {
      this.miningIndicator.destroy();
      this.miningIndicator = null;
    }
    if (this.miningFadeTimer) this.miningFadeTimer.destroy();
    if (this.miningProgressArc) {
      this.miningProgressArc.destroy();
      this.miningProgressArc = null;
    }
    if (this.warningShakeTween) {
      this.warningShakeTween.destroy();
      this.warningShakeTween = null;
    }
    if (this.warningFlashTween) {
      this.warningFlashTween.destroy();
      this.warningFlashTween = null;
    }
    if (this.warningFlash) {
      this.warningFlash.destroy();
      this.warningFlash = null;
    }
    if (this.asteroidSprite) {
      this.asteroidSprite.destroy();
      this.asteroidSprite = null;
    }
    super.destroy(fromScene);
  }
}
